import { subscribeToNativeCallEvents } from '../services/callRuntime';
import { useCallStore } from '../store/callStore';
import { sendCallConnected, sendCallFinalized } from '../services/callHistorySync';
import { isWebViewMode } from '../utils/webviewBridge';
import { applyNativeStateTransition, applyNativeUpgradeTransition, applySecurityVerifiedTransition } from './callStateTransitions';
import type { CallEndInfo } from './callTypes';
import { createIncomingActiveCall } from './callStateTransitions';

type CreateCallNativeEventHandlersParams = {
  navigate: (to: string) => void;
  handleCallEnd: (info?: CallEndInfo) => void;
};

export function subscribeToCallNativeEvents(params: CreateCallNativeEventHandlersParams): () => void {
  return subscribeToNativeCallEvents((event) => {
    const state = useCallStore.getState();
    let activeCall = state.activeCall;
    const incomingCall = state.incomingCall;

    if (event.type === 'finalized') {
      void sendCallFinalized({
        callSessionId: event.callSessionId,
        endedAt: Date.now(),
        finalStatus: event.finalStatus,
        durationSeconds: event.durationSeconds,
        reason: event.reason,
        localIdentityId: activeCall?.localIdentityId || incomingCall?.localIdentityId,
        serverUrl: activeCall?.serverUrl || incomingCall?.localServerUrl
      }).catch(() => {});
      return;
    }

    if (!activeCall && incomingCall?.callSessionId === event.callSessionId && event.type === 'state') {
      const hydratedIncomingCall = createIncomingActiveCall({
        callSessionId: incomingCall.callSessionId,
        remoteIdentityId: incomingCall.fromIdentityId,
        localIdentityId: incomingCall.localIdentityId,
        serverId: incomingCall.localServerId,
        serverName: incomingCall.localServerId,
        serverUrl: incomingCall.localServerUrl,
        isKnownContact: incomingCall.isKnownContact,
        isTemporaryLinkCall: incomingCall.isTemporaryLinkCall,
        remoteDisplayName: incomingCall.fromDisplayName,
        remoteIdentityName: incomingCall.fromIdentityName,
        remoteIdentityPublicKey: incomingCall.fromIdentityPublicKey,
        videoEnabled: event.localVideoEnabled === true,
        audioEnabled: event.muted !== true,
        securityVerified: incomingCall.e2eeVerified === true,
        status: 'connecting',
        startedAt: event.connectedAtMs && event.connectedAtMs > 0
          ? new Date(event.connectedAtMs)
          : undefined
      });
      useCallStore.getState().dispatchCallState({
        type: 'call/incoming-answer-started',
        call: hydratedIncomingCall
      });
      activeCall = useCallStore.getState().activeCall;
    }

    if (!activeCall || activeCall.callSessionId !== event.callSessionId) {
      return;
    }

    if (event.type === 'state') {
      if (
        event.connectedAtMs
        && event.connectedAtMs > 0
        && (event.state === 'connected' || event.state === 'active' || event.state === 'handoff')
      ) {
        void sendCallConnected(event.callSessionId, event.connectedAtMs, {
          localIdentityId: activeCall.localIdentityId,
          serverUrl: activeCall.serverUrl
        }).catch(() => {});
      }
      useCallStore.getState().dispatchCallState({
        type: 'call/native-event-applied',
        call: applyNativeStateTransition(activeCall, event)
      });
      const callState = useCallStore.getState();
      if (
        !isWebViewMode
        && !callState.isCallMinimized
        && (event.state === 'connecting' || event.state === 'connected' || event.state === 'active' || event.state === 'handoff')
        && window.location.pathname !== '/call'
      ) {
        params.navigate('/call');
      }
      return;
    }

    if (event.type === 'upgrade-to-video') {
      useCallStore.getState().dispatchCallState({
        type: 'call/native-upgrade-requested',
        call: applyNativeUpgradeTransition(activeCall)
      });
      if (!isWebViewMode) {
        params.navigate('/call');
      }
      return;
    }

    if (event.type === 'security-verified') {
      useCallStore.getState().dispatchCallState({
        type: 'call/security-verified',
        call: applySecurityVerifiedTransition(activeCall, event.verified)
      });
      return;
    }

    if (event.type === 'ended') {
      params.handleCallEnd({ reason: event.reason || 'native_ended', source: 'unknown' });
    }
  });
}
